import { useState } from "react";
import { FileDown, Pencil, MapPin, User, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import ConfirmDelete from "@/components/ConfirmDelete";
import { useApi, errorText } from "@/lib/api";
import { formatAUD, formatMoney } from "@/lib/format";
import { downloadBlob } from "@/lib/csv";

const Stat = ({ label, value, testId }) => (
  <div className="border border-border bg-background/60 p-3 rounded-md">
    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="font-mono text-base mt-0.5" data-testid={testId}>{value}</div>
  </div>
);

export default function ProjectPanel({ project, summary, onEdit, onDeleted }) {
  const api = useApi();
  const [withLedger, setWithLedger] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showLines, setShowLines] = useState(false);
  const pdf = async () => {
    setBusy(true);
    try {
      const blob = await api("get", `/projects/${project.id}/pdf?ledger=${withLedger}`, undefined, { responseType: "blob" });
      downloadBlob(`${project.name.replace(/[^A-Za-z0-9 _-]/g, "").trim().replace(/ /g, "-") || "Project"}-summary.pdf`, blob);
    } catch (e) { toast.error(errorText(e)); }
    setBusy(false);
  };
  const del = async () => {
    try { await api("delete", `/projects/${project.id}`); toast.success("Project deleted"); onDeleted(); }
    catch (e) { toast.error(errorText(e)); }
  };
  const s = summary;
  return (
    <section className="mt-6 border border-border bg-card p-5 rounded-lg space-y-5" data-testid="project-panel">
      <div className="flex flex-wrap items-start gap-3">
        <div className="space-y-1.5 min-w-0">
          <h2 className="font-display text-2xl font-semibold tracking-tight" data-testid="project-name">{project.name}</h2>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
            {project.address && <span className="flex items-center gap-1.5" data-testid="project-address"><MapPin className="h-3.5 w-3.5" />{project.address}</span>}
            {project.contact_name && <span className="flex items-center gap-1.5" data-testid="project-contact-name"><User className="h-3.5 w-3.5" />{project.contact_name}</span>}
            {project.contact_email && <a href={`mailto:${project.contact_email}`} className="flex items-center gap-1.5 hover:text-foreground" data-testid="project-contact-email"><Mail className="h-3.5 w-3.5" />{project.contact_email}</a>}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={onEdit} data-testid="project-edit-button"><Pencil className="h-4 w-4" /></Button>
          <ConfirmDelete name={project.name} testId="project-delete-button" onConfirm={del} />
        </div>
      </div>
      {s && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat label="Memo total (in PDF)" value={formatAUD(s.shared_total)} testId="project-shared-total" />
          <Stat label={`Internal notes (${s.internal_count})`} value={formatAUD(String(Number(s.notes_total) - Number(s.shared_total)))} testId="project-internal-total" />
          <Stat label="Ledger received" value={formatAUD(s.received)} testId="project-ledger-received" />
          <Stat label="Ledger spent" value={formatAUD(s.spent)} testId="project-ledger-spent" />
        </div>
      )}
      {s && (
        <div className="text-sm">
          <button className="text-muted-foreground underline-offset-4 hover:underline" onClick={() => setShowLines((v) => !v)} data-testid="project-ledger-toggle">
            {s.ledger.length} ledger line{s.ledger.length === 1 ? "" : "s"} with Project “{project.name}” {s.ledger.length ? (showLines ? "· hide" : "· show") : ""}
          </button>
          {showLines && s.ledger.length > 0 && (
            <table className="mt-3 w-full text-sm" data-testid="project-ledger-table">
              <tbody>
                {s.ledger.map((t) => (
                  <tr key={t.id} className="border-t border-border">
                    <td className="py-1.5 pr-3 font-mono text-xs whitespace-nowrap">{t.date}</td>
                    <td className="py-1.5 pr-3">{t.description}{t.what ? ` — ${t.what}` : ""}</td>
                    <td className="py-1.5 pr-3 text-right font-mono text-emerald-700 dark:text-emerald-400">{Number(t.received) ? formatMoney(t.received) : ""}</td>
                    <td className="py-1.5 text-right font-mono">{Number(t.spent) ? formatMoney(t.spent) : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-4 border-t border-border pt-4">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={withLedger} onCheckedChange={(v) => setWithLedger(!!v)} data-testid="project-pdf-ledger-checkbox" />
          Include ledger lines in the PDF
        </label>
        <Button className="ml-auto" onClick={pdf} disabled={busy} data-testid="project-pdf-button">
          <FileDown className="h-4 w-4 mr-1.5" /> {busy ? "Preparing…" : "Download summary PDF"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground -mt-2">The PDF shows the project details, memo notes not marked internal, and their total.</p>
    </section>
  );
}
